import React, { useState } from 'react';
import './RunnerSettings.css';

const RunnerSettings = ({ username: propUsername = '', password: propPassword = '' }) => {
  // Validate props
  const isValidUsername = propUsername && propUsername.trim().length > 0;
  const isValidPassword = propPassword && propPassword.trim().length > 0;
  const [showPassword, setShowPassword] = useState(false);
  const [username] = useState(isValidUsername ? propUsername : '');
  const [password] = useState(isValidPassword ? propPassword : '');
  const [copied, setCopied] = useState({ username: false, password: false });
  const [copiedCommand, setCopiedCommand] = useState('');
  const [selectedMethod, setSelectedMethod] = useState('git');
  const [selectedOS, setSelectedOS] = useState('linux');
  const [completedSteps, setCompletedSteps] = useState([]);
  const [installationStatus, setInstallationStatus] = useState('not-started'); // 'not-started', 'in-progress', 'completed', 'error'
  const [currentStep, setCurrentStep] = useState(0);
  const [error, setError] = useState(null);

  const handleCopy = async (field, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied({ ...copied, [field]: true });
      setTimeout(() => {
        setCopied(prev => ({ ...prev, [field]: false }));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = value;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied({ ...copied, [field]: true });
        setTimeout(() => {
          setCopied(prev => ({ ...prev, [field]: false }));
        }, 2000);
      } catch (err) {
        console.error('Fallback copy failed:', err);
      }
      document.body.removeChild(textArea);
    }
  };

  const handleCopyCommand = async (commandId, command) => {
    try {
      await navigator.clipboard.writeText(command);
      setCopiedCommand(commandId);
      setTimeout(() => {
        setCopiedCommand('');
      }, 2000);
    } catch (err) {
      console.error('Failed to copy command:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = command;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopiedCommand(commandId);
        setTimeout(() => {
          setCopiedCommand('');
        }, 2000);
      } catch (err) {
        console.error('Fallback copy failed:', err);
      }
      document.body.removeChild(textArea);
    }
  };

  // OS-specific command variations
  const getQuickStartCommand = () => {
    switch(selectedOS) {
      case 'windows':
        return 'powershell -Command "Invoke-WebRequest -Uri https://artemis-platform.com/install.ps1 -OutFile install.ps1; ./install.ps1"';
      case 'mac':
        return 'curl -sSL https://artemis-platform.com/install.sh | bash';
      default:
        return 'curl -sSL https://artemis-platform.com/install.sh | bash';
    }
  };

  const getDownloadCommand = () => {
    switch(selectedMethod) {
      case 'wget':
        if (selectedOS === 'windows') {
          return 'powershell -Command "Invoke-WebRequest -Uri https://artemis-platform.com/downloads/artemis-tools.zip -OutFile artemis-tools.zip"';
        }
        return 'wget https://artemis-platform.com/downloads/artemis-tools.zip';
      case 'curl':
        return 'curl -O https://artemis-platform.com/downloads/artemis-tools.zip';
      case 'tar':
        if (selectedOS === 'windows') {
          return 'powershell -Command "Invoke-WebRequest -Uri https://artemis-platform.com/downloads/artemis-tools.tar.gz -OutFile artemis-tools.tar.gz"';
        }
        return 'wget https://artemis-platform.com/downloads/artemis-tools.tar.gz';
      case 'git':
        return 'git clone https://github.com/artemis-platform/artemis-tools.git';
      default:
        return '';
    }
  };

  const getExtractCommand = () => {
    switch(selectedMethod) {
      case 'wget':
      case 'curl':
        if (selectedOS === 'windows') {
          return 'powershell -Command "Expand-Archive -Path artemis-tools.zip -DestinationPath ."';
        }
        return 'unzip artemis-tools.zip';
      case 'tar':
        if (selectedOS === 'windows') {
          return 'tar -xzf artemis-tools.tar.gz';
        }
        return 'tar -xzf artemis-tools.tar.gz';
      default:
        return '';
    }
  };

  const getPipCommand = () => {
    if (selectedOS === 'windows') {
      return 'pip install --find-links wheels\\ artemis-runner';
    }
    return 'pip install --find-links wheels/ artemis-runner';
  };

  // Track installation steps
  const trackStep = (stepNumber) => {
    if (!completedSteps.includes(stepNumber)) {
      setCompletedSteps(prev => [...prev, stepNumber]);
      setCurrentStep(stepNumber);
      
      // Simulate installation progress
      if (stepNumber === 1) {
        setInstallationStatus('in-progress');
      } else if (stepNumber === 5) {
        setInstallationStatus('completed');
      }
    }
  };

  // Simulate step completion when copying commands
  const handleCopyWithTracking = async (commandId, command, stepNumber) => {
    await handleCopyCommand(commandId, command);
    if (stepNumber) {
      trackStep(stepNumber);
    }
  };

  const getAllInOneCommand = () => {
    switch(selectedMethod) {
      case 'wget':
        if (selectedOS === 'windows') {
          return 'powershell -Command "Invoke-WebRequest -Uri https://artemis-platform.com/downloads/artemis-tools.zip -OutFile artemis-tools.zip"; powershell -Command "Expand-Archive -Path artemis-tools.zip -DestinationPath ."; cd artemis-tools; pip install --find-links wheels\\ artemis-runner';
        }
        return 'wget https://artemis-platform.com/downloads/artemis-tools.zip && unzip artemis-tools.zip && cd artemis-tools && pip install --find-links wheels/ artemis-runner';
      case 'curl':
        if (selectedOS === 'windows') {
          return 'powershell -Command "Invoke-WebRequest -Uri https://artemis-platform.com/downloads/artemis-tools.zip -OutFile artemis-tools.zip"; powershell -Command "Expand-Archive -Path artemis-tools.zip -DestinationPath ."; cd artemis-tools; pip install --find-links wheels\\ artemis-runner';
        }
        return 'curl -O https://artemis-platform.com/downloads/artemis-tools.zip && unzip artemis-tools.zip && cd artemis-tools && pip install --find-links wheels/ artemis-runner';
      case 'tar':
        if (selectedOS === 'windows') {
          return 'powershell -Command "Invoke-WebRequest -Uri https://artemis-platform.com/downloads/artemis-tools.tar.gz -OutFile artemis-tools.tar.gz"; tar -xzf artemis-tools.tar.gz; cd artemis-tools; pip install --find-links wheels\\ artemis-runner';
        }
        return 'wget https://artemis-platform.com/downloads/artemis-tools.tar.gz && tar -xzf artemis-tools.tar.gz && cd artemis-tools && pip install --find-links wheels/ artemis-runner';
      case 'git':
        if (selectedOS === 'windows') {
          return 'git clone https://github.com/artemis-platform/artemis-tools.git && cd artemis-tools && pip install --find-links wheels\\ artemis-runner';
        }
        return 'git clone https://github.com/artemis-platform/artemis-tools.git && cd artemis-tools && pip install --find-links wheels/ artemis-runner';
      default:
        return '';
    }
  };

  return (
    <div className="runner-settings-container">
      <div className="settings-header">
        <h2>Build Settings</h2>
        <div className="status-indicator">
          <svg className="check-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" fill="#4ade80"/>
            <path d="M8 12l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </div>

      <div className="settings-content">
        <div className="info-section">
          <div className="info-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="#22c55e" strokeWidth="2"/>
              <path d="M12 16v-4m0-4h.01" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="info-content">
            <h3>Quick Start: Setup your runner in 3 minutes</h3>
            <p>
              Setting up the Artemis runner is quick and easy. Just copy and paste a few commands!
            </p>

            <div className="requirements-box" style={{ marginTop: '20px', marginBottom: '20px' }}>
              <h4>System Requirements</h4>
              <ul className="requirements-list">
                <li>Python 3.11 or higher</li>
                <li>pip package manager</li>
                <li>2GB of free disk space</li>
                <li>Internet connection for initial setup</li>
              </ul>
            </div>

            <div style={{
              background: '#3b82f6',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '15px'
            }}>
              <svg viewBox="0 0 24 24" fill="none" style={{ width: '24px', height: '24px', flexShrink: 0 }}>
                <path d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" fill="white"/>
                <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="white" strokeWidth="2"/>
              </svg>
              <div style={{ flex: 1 }}>
                <h4 style={{ margin: 0, color: 'white', fontSize: '16px' }}>Watch Installation Video</h4>
                <p style={{ margin: '4px 0 0 0', color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px' }}>
                  See a step-by-step walkthrough of the installation process
                </p>
              </div>
              <a 
                href="https://www.youtube.com/watch?v=artemis-install" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{
                  background: 'white',
                  color: '#3b82f6',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  textDecoration: 'none',
                  fontWeight: '600',
                  fontSize: '14px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                Watch Video
                <svg viewBox="0 0 24 24" fill="none" style={{ width: '16px', height: '16px' }}>
                  <path d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </a>
            </div>
            
            <div className="quick-start-box">
              <div className="quick-start-header">
                <svg className="quick-start-icon" viewBox="0 0 24 24" fill="none">
                  <path d="M13 10V3L4 14h7v7l9-11h-7z" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="#fef3c7"/>
                </svg>
                <span className="quick-start-title">Quick Start</span>
              </div>
              
              <div className="os-selector">
                <button 
                  className={`os-button ${selectedOS === 'linux' ? 'active' : ''}`}
                  onClick={() => setSelectedOS('linux')}
                  aria-label="Select Linux/WSL operating system"
                  aria-pressed={selectedOS === 'linux'}
                >
                  🐧 Linux/WSL
                </button>
                <button 
                  className={`os-button ${selectedOS === 'mac' ? 'active' : ''}`}
                  onClick={() => setSelectedOS('mac')}
                  aria-label="Select macOS operating system"
                  aria-pressed={selectedOS === 'mac'}
                >
                  🍎 macOS
                </button>
                <button 
                  className={`os-button ${selectedOS === 'windows' ? 'active' : ''}`}
                  onClick={() => setSelectedOS('windows')}
                  aria-label="Select Windows operating system"
                  aria-pressed={selectedOS === 'windows'}
                >
                  🪟 Windows
                </button>
              </div>
              
              <p className="quick-start-text">
                Copy and run this single command to install everything:
              </p>
              <div className="command-box highlight">
                <code>{getQuickStartCommand()}</code>
                <button 
                  className="copy-command-button highlight"
                  onClick={() => handleCopyWithTracking('quickstart', getQuickStartCommand(), 1)}
                >
                  {copiedCommand === 'quickstart' ? '✓ Copied!' : 'Copy & Run'}
                </button>
              </div>
            </div>
            
            <div className="progress-indicator">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${(completedSteps.length / 5) * 100}%` }}
                />
              </div>
              <p className="progress-text">
                {completedSteps.length === 0 && installationStatus === 'not-started' && "Ready to start installation"}
                {completedSteps.length > 0 && completedSteps.length < 5 && `Step ${completedSteps.length} of 5 completed`}
                {completedSteps.length === 5 && "✅ Installation complete!"}
                {installationStatus === 'error' && `❌ Installation error: ${error}`}
              </p>
            </div>
            
            <ol className="setup-steps">
              <li>
                <strong>Choose your download method:</strong>
                
                <div className="method-selector">
                  <button 
                    className={`method-button ${selectedMethod === 'wget' ? 'active' : ''}`}
                    onClick={() => setSelectedMethod('wget')}
                  >
                    <svg className="method-icon" viewBox="0 0 24 24" fill="none">
                      <path d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {selectedOS === 'windows' ? 'PowerShell' : 'wget'}
                  </button>
                  <button 
                    className={`method-button ${selectedMethod === 'curl' ? 'active' : ''}`}
                    onClick={() => setSelectedMethod('curl')}
                  >
                    <svg className="method-icon" viewBox="0 0 24 24" fill="none">
                      <path d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    curl
                  </button>
                  <button 
                    className={`method-button ${selectedMethod === 'tar' ? 'active' : ''}`}
                    onClick={() => setSelectedMethod('tar')}
                  >
                    <svg className="method-icon" viewBox="0 0 24 24" fill="none">
                      <path d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    tar.gz
                  </button>
                  <button 
                    className={`method-button ${selectedMethod === 'git' ? 'active' : ''}`}
                    onClick={() => setSelectedMethod('git')}
                  >
                    <svg className="method-icon" viewBox="0 0 24 24" fill="none">
                      <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 00-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0020 4.77 5.07 5.07 0 0019.91 1S18.73.65 16 2.48a13.38 13.38 0 00-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 005 4.77a5.44 5.44 0 00-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 009 18.13V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Git Clone
                  </button>
                </div>

                <div className="command-section">
                  <div className="command-box">
                    <code>{getDownloadCommand()}</code>
                    <button 
                      className="copy-command-button"
                      onClick={() => handleCopyWithTracking('download', getDownloadCommand(), 2)}
                      title="Copy command"
                    >
                      {copiedCommand === 'download' ? '✓' : 'Copy'}
                    </button>
                  </div>
                </div>
              </li>
              <li>
                <strong>Use these credentials to authenticate:</strong>
                
                <div className="credentials-section">
                  <div className="credential-field">
                    <label>Username:</label>
                    <div className="credential-input">
                      <input 
                        type="text" 
                        value={username || 'runner_user_12345'}
                        readOnly
                        className={username ? '' : 'placeholder-text'}
                      />
                      <button 
                        className="icon-button"
                        onClick={() => handleCopy('username', username || 'runner_user_12345')}
                        title="Copy username"
                        aria-label="Copy username to clipboard"
                      >
                        {copied.username ? (
                          <svg className="icon" viewBox="0 0 24 24" fill="none">
                            <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        ) : (
                          <svg className="icon" viewBox="0 0 24 24" fill="none">
                            <rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="2"/>
                            <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" stroke="currentColor" strokeWidth="2"/>
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                  
                  <div className="credential-field">
                    <label>Password:</label>
                    <div className="credential-input">
                      <input 
                        type={showPassword ? "text" : "password"}
                        value={showPassword ? (password || 'xK9#mP2$vL6@nQ8*rT4&jH7!') : (password || '••••••••••••••••••••••••••')}
                        readOnly
                        className={password ? '' : 'placeholder-text'}
                      />
                      <button 
                        className="icon-button"
                        onClick={() => setShowPassword(!showPassword)}
                        title={showPassword ? "Hide password" : "Show password"}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? (
                          <svg className="icon" viewBox="0 0 24 24" fill="none">
                            <path d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        ) : (
                          <svg className="icon" viewBox="0 0 24 24" fill="none">
                            <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" stroke="currentColor" strokeWidth="2"/>
                            <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" stroke="currentColor" strokeWidth="2"/>
                          </svg>
                        )}
                      </button>
                      <button 
                        className="icon-button"
                        onClick={() => handleCopy('password', password || 'xK9#mP2$vL6@nQ8*rT4&jH7!')}
                        title="Copy password"
                        aria-label="Copy password to clipboard"
                      >
                        {copied.password ? (
                          <svg className="icon" viewBox="0 0 24 24" fill="none">
                            <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        ) : (
                          <svg className="icon" viewBox="0 0 24 24" fill="none">
                            <rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="2"/>
                            <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" stroke="currentColor" strokeWidth="2"/>
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </li>
              <li>
                <strong>Install the runner with these simple commands:</strong>
                
                {(selectedMethod === 'wget' || selectedMethod === 'curl' || selectedMethod === 'tar') && (
                  <div className="commands-list">
                    {(selectedMethod === 'wget' || selectedMethod === 'curl') && (
                      <div className="command-box">
                        <code>{getExtractCommand()}</code>
                        <button 
                          className="copy-command-button"
                          onClick={() => handleCopyCommand('extract', getExtractCommand())}
                        >
                          {copiedCommand === 'extract' ? '✓' : 'Copy'}
                        </button>
                      </div>
                    )}
                    {selectedMethod === 'tar' && (
                      <div className="command-box">
                        <code>{getExtractCommand()}</code>
                        <button 
                          className="copy-command-button"
                          onClick={() => handleCopyCommand('extract-tar', getExtractCommand())}
                        >
                          {copiedCommand === 'extract-tar' ? '✓' : 'Copy'}
                        </button>
                      </div>
                    )}
                    <div className="command-box">
                      <code>cd artemis-tools</code>
                      <button 
                        className="copy-command-button"
                        onClick={() => handleCopyCommand('cd', 'cd artemis-tools')}
                      >
                        {copiedCommand === 'cd' ? '✓' : 'Copy'}
                      </button>
                    </div>
                    <div className="command-box">
                      <code>{getPipCommand()}</code>
                      <button 
                        className="copy-command-button"
                        onClick={() => handleCopyCommand('pip', getPipCommand())}
                      >
                        {copiedCommand === 'pip' ? '✓' : 'Copy'}
                      </button>
                    </div>
                  </div>
                )}

                {selectedMethod === 'git' && (
                  <div className="commands-list">
                    <div className="command-box">
                      <code>cd artemis-tools</code>
                      <button 
                        className="copy-command-button"
                        onClick={() => handleCopyCommand('cd-git', 'cd artemis-tools')}
                      >
                        {copiedCommand === 'cd-git' ? '✓' : 'Copy'}
                      </button>
                    </div>
                    <div className="command-box">
                      <code>{getPipCommand()}</code>
                      <button 
                        className="copy-command-button"
                        onClick={() => handleCopyCommand('pip-git', getPipCommand())}
                      >
                        {copiedCommand === 'pip-git' ? '✓' : 'Copy'}
                      </button>
                    </div>
                  </div>
                )}

                <div className="all-in-one-section">
                  <p className="all-in-one-label">Or copy all commands at once:</p>
                  <div className="command-box all-in-one">
                    <code>{getAllInOneCommand()}</code>
                    <button 
                      className="copy-command-button"
                      onClick={() => handleCopyWithTracking('all', getAllInOneCommand(), 4)}
                    >
                      {copiedCommand === 'all' ? '✓' : 'Copy All'}
                    </button>
                  </div>
                </div>
              </li>
              <li>
                <strong>That's it!</strong> Your runner is now installed and ready to use. 
                <a href="#" className="link"> Learn more about configuring runners</a>.
              </li>
              <li>
                <strong>Run the runner:</strong>
                
                <div className="command-section" style={{ marginTop: '12px' }}>
                  <div className="command-box">
                    <code>artemis-runner --runner-name {username || 'runner_user_12345'}</code>
                    <button 
                      className="copy-command-button"
                      onClick={() => handleCopyWithTracking('run-runner', `artemis-runner --runner-name ${username || 'runner_user_12345'}`, 5)}
                      title="Copy command"
                    >
                      {copiedCommand === 'run-runner' ? '✓' : 'Copy'}
                    </button>
                  </div>
                  <p style={{ 
                    fontSize: '13px', 
                    color: 'rgba(255, 255, 255, 0.7)', 
                    marginTop: '8px',
                    marginBottom: '0'
                  }}>
                    The runner will start listening for tasks assigned to your user ID
                  </p>
                </div>
              </li>
            </ol>
            
            <div className="runner-architecture-section">
              <h3 style={{ 
                margin: '30px 0 20px 0', 
                fontSize: '22px',
                fontWeight: '600',
                color: '#1f2937'
              }}>
                How Artemis Works
              </h3>
              
              <div style={{
                background: 'white',
                borderRadius: '8px',
                padding: '30px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
              }}>
                {/* Architecture Overview */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 80px 1fr',
                  gap: '20px',
                  marginBottom: '40px',
                  alignItems: 'stretch'
                }}>
                  {/* Artemis Server */}
                  <div style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: '12px',
                    padding: '24px',
                    textAlign: 'center',
                    position: 'relative',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    border: '2px solid #667eea',
                    color: 'white'
                  }}>
                    <div style={{
                      position: 'absolute',
                      top: '-10px',
                      right: '-10px',
                      background: '#10b981',
                      borderRadius: '50%',
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid #ffffff'
                    }}>
                      <div style={{
                        width: '8px',
                        height: '8px',
                        background: 'white',
                        borderRadius: '50%'
                      }}></div>
                    </div>
                    <h4 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '700', color: '#ffffff' }}>
                      ☁️ Artemis Server
                    </h4>
                    <p style={{ fontSize: '13px', marginBottom: '20px', color: 'rgba(255, 255, 255, 0.9)' }}>
                      Cloud Infrastructure
                    </p>
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: '8px',
                      padding: '12px',
                      marginBottom: '10px',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      backdropFilter: 'blur(10px)'
                    }}>
                      <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '4px', color: '#ffffff' }}>
                        📁 Your Repository
                      </div>
                      <div style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.8)' }}>
                        Source code & configs
                      </div>
                    </div>
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: '8px',
                      padding: '12px',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      backdropFilter: 'blur(10px)'
                    }}>
                      <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '4px', color: '#ffffff' }}>
                        📡 Message Queue
                      </div>
                      <div style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.8)' }}>
                        Commands waiting
                      </div>
                    </div>
                  </div>
                  
                  {/* Connection */}
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'relative'
                  }}>
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '100%',
                      height: '2px',
                      background: 'linear-gradient(90deg, #667eea 0%, #3b82f6 100%)'
                    }}></div>
                    <div style={{
                      background: '#ffffff',
                      border: '2px solid #667eea',
                      borderRadius: '6px',
                      padding: '6px 12px',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#667eea',
                      zIndex: 1,
                      whiteSpace: 'nowrap'
                    }}>
                      WebSocket
                    </div>
                  </div>
                  
                  {/* Your Machine */}
                  <div style={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    borderRadius: '12px',
                    padding: '24px',
                    color: 'white',
                    textAlign: 'center',
                    position: 'relative',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    border: '2px solid #3b82f6'
                  }}>
                    <div style={{
                      position: 'absolute',
                      top: '-10px',
                      right: '-10px',
                      background: '#10b981',
                      borderRadius: '50%',
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      animation: 'pulse 2s infinite',
                      border: '2px solid #ffffff'
                    }}>
                      <div style={{
                        width: '8px',
                        height: '8px',
                        background: 'white',
                        borderRadius: '50%'
                      }}></div>
                    </div>
                    <h4 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '700' }}>
                      💻 Your Machine
                    </h4>
                    <p style={{ fontSize: '13px', marginBottom: '20px', opacity: 0.8 }}>
                      Local Environment
                    </p>
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: '8px',
                      padding: '12px',
                      marginBottom: '10px',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      backdropFilter: 'blur(10px)'
                    }}>
                      <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>
                        🏃 Runner Process
                      </div>
                      <div style={{ fontSize: '11px', opacity: 0.8 }}>
                        Listening on channel
                      </div>
                    </div>
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      color: '#ffffff',
                      padding: '10px',
                      borderRadius: '6px',
                      fontFamily: 'monospace',
                      fontSize: '11px',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      backdropFilter: 'blur(10px)'
                    }}>
                      artemis-runner --runner-name {username || 'user_12345'}
                    </div>
                  </div>
                </div>
                
                {/* Step by Step Flow */}
                <div style={{
                  background: '#f8fafc',
                  borderRadius: '12px',
                  padding: '24px',
                  marginBottom: '25px',
                  border: '2px solid #e5e7eb'
                }}>
                  <h5 style={{ margin: '0 0 20px 0', fontSize: '18px', color: '#1f2937', fontWeight: '700' }}>
                    📋 Step-by-Step Flow
                  </h5>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {/* Step 1 */}
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                      <div style={{
                        background: '#667eea',
                        color: '#ffffff',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        fontWeight: '700',
                        flexShrink: 0,
                        border: 'none'
                      }}>
                        1
                      </div>
                      <div style={{ flex: 1 }}>
                        <strong style={{ color: '#1f2937', fontSize: '15px' }}>Command Triggered</strong>
                        <div style={{ color: '#6b7280', marginTop: '4px', fontSize: '13px' }}>
                          You click a build/run command in Artemis UI
                        </div>
                        <div style={{
                          background: '#f9fafb',
                          padding: '10px',
                          borderRadius: '6px',
                          marginTop: '8px',
                          fontSize: '12px',
                          color: '#374151',
                          border: '1px solid #e5e7eb'
                        }}>
                          Example: "Build Project" → Command queued on server
                        </div>
                      </div>
                    </div>
                    
                    {/* Step 2 */}
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                      <div style={{
                        background: '#667eea',
                        color: '#ffffff',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        fontWeight: '700',
                        flexShrink: 0,
                        border: 'none'
                      }}>
                        2
                      </div>
                      <div style={{ flex: 1 }}>
                        <strong style={{ color: '#1f2937', fontSize: '15px' }}>Runner Receives Command</strong>
                        <div style={{ color: '#6b7280', marginTop: '4px', fontSize: '13px' }}>
                          Your local runner picks up the command from its channel
                        </div>
                        <div style={{
                          background: '#f9fafb',
                          padding: '10px',
                          borderRadius: '6px',
                          marginTop: '8px',
                          fontSize: '12px',
                          color: '#374151',
                          border: '1px solid #e5e7eb'
                        }}>
                          Runner listening on: <code style={{ background: '#e5e7eb', padding: '2px 4px', borderRadius: '3px' }}>channel:{username || 'user_12345'}</code>
                        </div>
                      </div>
                    </div>
                    
                    {/* Step 3 */}
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                      <div style={{
                        background: '#3b82f6',
                        color: '#ffffff',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        fontWeight: '700',
                        flexShrink: 0
                      }}>
                        3
                      </div>
                      <div style={{ flex: 1 }}>
                        <strong style={{ color: '#1f2937', fontSize: '15px' }}>Repository Copied Locally</strong>
                        <div style={{ color: '#6b7280', marginTop: '4px', fontSize: '13px' }}>
                          Runner clones your repository to a temporary folder
                        </div>
                        <code style={{
                          background: '#f3f4f6',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          display: 'inline-block',
                          marginTop: '8px',
                          fontFamily: 'monospace',
                          border: '1px solid #e5e7eb'
                        }}>
                          /tmp/artemis-work/{username || 'user_12345'}/project-{'{timestamp}'}
                        </code>
                        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                          Or custom path with: <code style={{ background: '#e5e7eb', padding: '2px 4px', borderRadius: '3px' }}>--worker-output-root /my/path</code>
                        </div>
                      </div>
                    </div>
                    
                    {/* Step 4 */}
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                      <div style={{
                        background: '#3b82f6',
                        color: '#ffffff',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        fontWeight: '700',
                        flexShrink: 0
                      }}>
                        4
                      </div>
                      <div style={{ flex: 1 }}>
                        <strong style={{ color: '#1f2937', fontSize: '15px' }}>Command Executed</strong>
                        <div style={{ color: '#6b7280', marginTop: '4px', fontSize: '13px' }}>
                          Runner executes the command in the local copy
                        </div>
                        <div style={{
                          background: '#1e293b',
                          color: '#e2e8f0',
                          padding: '10px',
                          borderRadius: '6px',
                          fontFamily: 'monospace',
                          fontSize: '11px',
                          marginTop: '8px',
                          border: '1px solid #334155'
                        }}>
                          $ cd /tmp/artemis-work/.../project<br/>
                          $ mvn clean install
                        </div>
                      </div>
                    </div>
                    
                    {/* Step 5 */}
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                      <div style={{
                        background: '#10b981',
                        color: 'white',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        fontWeight: '700',
                        flexShrink: 0
                      }}>
                        5
                      </div>
                      <div style={{ flex: 1 }}>
                        <strong style={{ color: '#1f2937', fontSize: '15px' }}>Logs Sent Back</strong>
                        <div style={{ color: '#6b7280', marginTop: '4px', fontSize: '13px' }}>
                          Real-time logs stream back to Artemis server
                        </div>
                        <div style={{
                          background: '#d1fae5',
                          padding: '10px',
                          borderRadius: '6px',
                          marginTop: '8px',
                          fontSize: '12px',
                          color: '#065f46',
                          border: '1px solid #86efac'
                        }}>
                          ✅ Build successful! Logs visible in Artemis UI
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Advanced Configuration */}
                <div style={{
                  borderTop: '1px solid #e5e7eb',
                  paddingTop: '25px'
                }}>
                  <h5 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#1f2937', fontWeight: '600' }}>
                    ⚙️ Advanced Configuration
                  </h5>
                  <div style={{
                    background: '#f3f4f6',
                    borderRadius: '6px',
                    padding: '15px',
                    fontFamily: 'monospace',
                    fontSize: '13px'
                  }}>
                    <div style={{ marginBottom: '10px' }}>
                      <span style={{ color: '#059669' }}>--worker-output-root /my/custom/path</span>
                      <span style={{ color: '#6b7280', marginLeft: '10px' }}>
                        # Change where repos are cloned
                      </span>
                    </div>
                    <div>
                      <span style={{ color: '#059669' }}>--environment https://custom.artemis-server.com</span>
                      <span style={{ color: '#6b7280', marginLeft: '10px' }}>
                        # Connect to custom Artemis instance
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="troubleshooting-box">
              <h4>🔧 Common Issues & Solutions</h4>
              
              <div className="troubleshooting-item">
                <strong>Permission Denied Error</strong>
                <p>If you get a permission error when running commands:</p>
                <div className="command-box">
                  <code>sudo pip install --find-links wheels/ artemis-runner</code>
                  <button 
                    className="copy-command-button"
                    onClick={() => handleCopyCommand('sudo-pip', 'sudo pip install --find-links wheels/ artemis-runner')}
                  >
                    {copiedCommand === 'sudo-pip' ? '✓' : 'Copy'}
                  </button>
                </div>
                <p className="inline-code">Or use --user flag: pip install --user ...</p>
              </div>
              
              <div className="troubleshooting-item">
                <strong>Python Version Issues</strong>
                <p>If you get a Python version error, check your version:</p>
                <div className="command-box">
                  <code>python --version</code>
                  <button 
                    className="copy-command-button"
                    onClick={() => handleCopyCommand('python-version', 'python --version')}
                  >
                    {copiedCommand === 'python-version' ? '✓' : 'Copy'}
                  </button>
                </div>
                <p>Artemis requires Python 3.11 or higher. Use <span className="inline-code">python3</span> or <span className="inline-code">python3.11</span> if needed.</p>
              </div>
              
              <div className="troubleshooting-item">
                <strong>Connection Timeout</strong>
                <p>If the runner can't connect to the server:</p>
                <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                  <li>Check your internet connection</li>
                  <li>Verify firewall settings allow WebSocket connections</li>
                  <li>Try using a different network</li>
                  <li>Contact IT if you're behind a corporate proxy</li>
                </ul>
              </div>
              
              <div className="troubleshooting-item">
                <strong>Missing Dependencies</strong>
                <p>If you get module not found errors:</p>
                <div className="command-box">
                  <code>pip install --upgrade pip setuptools wheel</code>
                  <button 
                    className="copy-command-button"
                    onClick={() => handleCopyCommand('upgrade-pip', 'pip install --upgrade pip setuptools wheel')}
                  >
                    {copiedCommand === 'upgrade-pip' ? '✓' : 'Copy'}
                  </button>
                </div>
              </div>
            </div>

            <div className="environment-variables-section" style={{ marginTop: '30px' }}>
              <h3 style={{ 
                margin: '0 0 20px 0', 
                fontSize: '22px',
                fontWeight: '600',
                color: '#1f2937'
              }}>
                🌐 Environment Variables
              </h3>
              
              <div style={{
                background: 'white',
                borderRadius: '8px',
                padding: '24px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
              }}>
                <p style={{ marginBottom: '20px', color: '#6b7280' }}>
                  Configure your runner behavior with these environment variables:
                </p>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div style={{
                    background: '#f9fafb',
                    borderRadius: '6px',
                    padding: '16px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <code style={{ fontWeight: '600', color: '#059669' }}>ARTEMIS_API_KEY</code>
                      <span style={{ fontSize: '12px', color: '#ef4444', fontWeight: '500' }}>Required</span>
                    </div>
                    <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#6b7280' }}>
                      Your Artemis API key for authentication
                    </p>
                    <div className="command-box">
                      <code>export ARTEMIS_API_KEY="your-api-key-here"</code>
                      <button 
                        className="copy-command-button"
                        onClick={() => handleCopyCommand('env-api-key', 'export ARTEMIS_API_KEY="your-api-key-here"')}
                      >
                        {copiedCommand === 'env-api-key' ? '✓' : 'Copy'}
                      </button>
                    </div>
                  </div>
                  
                  <div style={{
                    background: '#f9fafb',
                    borderRadius: '6px',
                    padding: '16px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <code style={{ fontWeight: '600', color: '#059669' }}>ARTEMIS_WORKER_DIR</code>
                      <span style={{ fontSize: '12px', color: '#3b82f6', fontWeight: '500' }}>Optional</span>
                    </div>
                    <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#6b7280' }}>
                      Custom directory for cloning repositories (default: /tmp/artemis-work)
                    </p>
                    <div className="command-box">
                      <code>export ARTEMIS_WORKER_DIR="/home/user/artemis-work"</code>
                      <button 
                        className="copy-command-button"
                        onClick={() => handleCopyCommand('env-worker-dir', 'export ARTEMIS_WORKER_DIR="/home/user/artemis-work"')}
                      >
                        {copiedCommand === 'env-worker-dir' ? '✓' : 'Copy'}
                      </button>
                    </div>
                  </div>
                  
                  <div style={{
                    background: '#f9fafb',
                    borderRadius: '6px',
                    padding: '16px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <code style={{ fontWeight: '600', color: '#059669' }}>ARTEMIS_LOG_LEVEL</code>
                      <span style={{ fontSize: '12px', color: '#3b82f6', fontWeight: '500' }}>Optional</span>
                    </div>
                    <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#6b7280' }}>
                      Set logging verbosity (DEBUG, INFO, WARNING, ERROR)
                    </p>
                    <div className="command-box">
                      <code>export ARTEMIS_LOG_LEVEL="DEBUG"</code>
                      <button 
                        className="copy-command-button"
                        onClick={() => handleCopyCommand('env-log-level', 'export ARTEMIS_LOG_LEVEL="DEBUG"')}
                      >
                        {copiedCommand === 'env-log-level' ? '✓' : 'Copy'}
                      </button>
                    </div>
                  </div>
                  
                  <div style={{
                    background: '#f9fafb',
                    borderRadius: '6px',
                    padding: '16px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <code style={{ fontWeight: '600', color: '#059669' }}>HTTPS_PROXY / HTTP_PROXY</code>
                      <span style={{ fontSize: '12px', color: '#3b82f6', fontWeight: '500' }}>Optional</span>
                    </div>
                    <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#6b7280' }}>
                      Configure proxy settings for corporate networks
                    </p>
                    <div className="command-box">
                      <code>export HTTPS_PROXY="http://proxy.company.com:8080"</code>
                      <button 
                        className="copy-command-button"
                        onClick={() => handleCopyCommand('env-proxy', 'export HTTPS_PROXY="http://proxy.company.com:8080"')}
                      >
                        {copiedCommand === 'env-proxy' ? '✓' : 'Copy'}
                      </button>
                    </div>
                  </div>
                </div>
                
                <div style={{
                  marginTop: '24px',
                  padding: '16px',
                  background: '#eff6ff',
                  borderRadius: '6px',
                  border: '1px solid #3b82f6'
                }}>
                  <p style={{ margin: 0, fontSize: '14px', color: '#1e40af' }}>
                    <strong>Tip:</strong> Add these to your <code style={{ background: '#dbeafe', padding: '2px 4px', borderRadius: '3px' }}>~/.bashrc</code> or <code style={{ background: '#dbeafe', padding: '2px 4px', borderRadius: '3px' }}>~/.zshrc</code> to persist across sessions.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="help-section">
              <div className="help-content">
                <svg className="help-icon" viewBox="0 0 24 24" fill="none">
                  <path d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <div>
                  <strong>Need help?</strong> Check our <a href="https://docs.artemis-platform.com" className="link">documentation</a> or contact <a href="mailto:support@artemis-platform.com" className="link">support@artemis-platform.com</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RunnerSettings; 